import { Box, Flex, useBreakpoints } from "@otrium/core";
import { unescape } from "lodash-es";
import Image from "next/image";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import { cmsImageLoader } from "src/utils/cms-image-loader";
import { Skeleton } from "src/atoms/Skeleton";
import { useImageLoaded } from "src/hooks/use-image-loaded";
import { useInView } from "react-intersection-observer";
import {
  PromotionDisplayType,
  PromotionType,
  ShopType,
} from "src/types/graphql.d";
import { localeFixOptional } from "src/utils/genderLocale/genderLocale";
import { stripPageMarkerFromParam } from "src/utils/pagination/page-url";
import { getFileName } from "src/utils/getFileName";
import { PromotionEventType, useSegment } from "src/hooks/useSegment";
import { SEGMENT_IN_VIEW_THRESHOLD, SEGMENT_PROMOTION_TYPE } from "src/segment";
import { usePromotion } from "src/hooks/usePromotion";
import { PromotionalBlock } from "../PromotionalBlock";
import { theme } from "src/theme";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { PromotionBlockSkeleton } from "src/organisms/ProductList/PromotionBlockSkeleton";
import Cookies from "js-cookie";
import { OT_SHOP_TYPE_COOKIE } from "src/utils/constants";
import { useUserPreferences } from "src/hooks/stores/use-app-store";
import { PromoTimer } from "../PromoTimer";

type BrandsPromoProps = {
  skipTrackingViewed?: boolean;
  slug: string;
  promotionType?: PromotionType;
  callback?: (loading: boolean) => void;
};

type PromoBannerImageProps = {
  imageSrc: string;
  message?: string | null;
  height: string;
  borderRadius: string;
};

const TIMER_BANNERS: PromotionDisplayType[] = [
  PromotionDisplayType.FlashSale,
  PromotionDisplayType.LightningDeal,
];

const PromoBannerImage = ({
  imageSrc,
  message,
  height,
}: PromoBannerImageProps) => {
  const { imgRef, loaded, onLoad } = useImageLoaded();

  return (
    <Box
      style={{
        position: "relative",
        width: "100%",
        height,
        minHeight: theme.space.space96,
      }}
    >
      {!loaded && <Skeleton style={{ position: "absolute", inset: 0 }} />}
      <Image
        ref={imgRef}
        loader={cmsImageLoader}
        src={imageSrc}
        alt={message || "Promotion banner"}
        fill
        sizes="100vw"
        onLoad={onLoad}
        style={{
          objectFit: "cover",
          opacity: loaded ? 1 : 0,
          transition: "opacity 300ms ease",
        }}
      />
    </Box>
  );
};

const BrandsPromo = ({
  skipTrackingViewed,
  slug,
  promotionType,
  callback,
}: BrandsPromoProps): JSX.Element | null => {
  const { isDesktop } = useBreakpoints();
  const router = useRouter();
  const userPreferences = useUserPreferences();
  const routeSlug = useMemo(
    () => stripPageMarkerFromParam(router.query.slug).value,
    [router.query.slug]
  );

  const genderFromUrl = useMemo(() => {
    if (typeof routeSlug === "string") {
      return routeSlug;
    }

    if (Array.isArray(routeSlug)) {
      return routeSlug.find((segment) =>
        localeFixOptional({ gender: segment })
      );
    }

    return undefined;
  }, [routeSlug]);

  const localeGenderId = useMemo(() => {
    const shopTypeFromUrl = localeFixOptional({ gender: genderFromUrl });
    if (shopTypeFromUrl) return shopTypeFromUrl;

    const preferredShopType = localeFixOptional({
      gender: userPreferences?.gender,
    });
    if (preferredShopType) return preferredShopType;

    const cookieShopType = localeFixOptional({
      gender: Cookies.get(OT_SHOP_TYPE_COOKIE),
    });
    if (cookieShopType) return cookieShopType;

    return ShopType.Women;
  }, [genderFromUrl, userPreferences?.gender]);
  const { data: promoData, loading } = usePromotion(
    slug,
    promotionType || PromotionType.Category,
    localeGenderId
  );
  const isLoggedIn = useIsLoggedIn();
  const { segmentPromotionClickedOrViewed } = useSegment();
  const { enableGatedHomepage } = useFeatureFlags();

  const promotion = promoData?.promotion;

  const getPromoByGender = useCallback(() => {
    switch (localeGenderId) {
      case "women":
        return promotion?.promo_url_women;
      case "men":
        return promotion?.promo_url_men;
      default:
        return promotion?.promo_url_kids;
    }
  }, [promotion, localeGenderId]);

  const promoBannerUrl = unescape(getPromoByGender() || "");

  const trackingData = useMemo(() => {
    return {
      name: promotion?.promo_name,
      creative: getFileName(promoBannerUrl),
      position: "plp-page-promo-banner",
      promotionType: SEGMENT_PROMOTION_TYPE.plpPromoBanner,
      gated: !!(!isLoggedIn && enableGatedHomepage),
    };
  }, [promoBannerUrl, promotion, isLoggedIn, enableGatedHomepage]);

  const promotionClickedOrViewed = useCallback(
    (eventType: PromotionEventType) => {
      void segmentPromotionClickedOrViewed({
        ...trackingData,
        eventType,
      });
    },
    [trackingData, segmentPromotionClickedOrViewed]
  );

  const { ref: promoBannerRef } = useInView({
    triggerOnce: true,
    skip: skipTrackingViewed,
    threshold: SEGMENT_IN_VIEW_THRESHOLD,
    onChange(inView) {
      if (inView) {
        promotionClickedOrViewed(PromotionEventType.view);
      }
    },
  });

  const promoClickHandler = useCallback(() => {
    promotionClickedOrViewed(PromotionEventType.click);
  }, [promotionClickedOrViewed]);

  useEffect(() => {
    if (callback && !loading && (!promoData || !promoData?.promotion)) {
      callback(false);
    }
  }, [callback, loading, promoData]);

  if (loading) {
    return <PromotionBlockSkeleton />;
  }

  if (!promotion) {
    return null;
  }

  const hasPromoMessage =
    promotion.promo_display_type !== PromotionDisplayType.FlashSale;
  const promoImage = isDesktop
    ? promotion.promo_image_desktop || promotion.promo_image_mobile
    : promotion.promo_image_mobile || promotion.promo_image_desktop;
  const hasPromoBanner = Boolean(promoImage);
  const promoContainerHeight = hasPromoBanner
    ? hasPromoMessage
      ? "190px"
      : "150px"
    : "40px";

  if (
    !hasPromoBanner &&
    !hasPromoMessage &&
    promotion.promo_display_type &&
    !TIMER_BANNERS.includes(promotion.promo_display_type)
  ) {
    return null;
  }

  return (
    <Flex
      ref={promoBannerRef}
      mb={2}
      flexWrap="wrap"
      flexDirection="column"
      data-testid="promo-container"
      height={promoContainerHeight}
      overflow="hidden"
    >
      {hasPromoBanner &&
        (promoBannerUrl ? (
          <a href={promoBannerUrl} onClick={promoClickHandler}>
            <PromoBannerImage
              imageSrc={promoImage as string}
              message={promotion.promo_message}
              height="150px"
              borderRadius={hasPromoMessage ? "8px 8px 0 0" : "8px"}
            />
          </a>
        ) : (
          <PromoBannerImage
            imageSrc={promoImage as string}
            message={promotion.promo_message}
            height="150px"
            borderRadius={hasPromoMessage ? "8px 8px 0 0" : "8px"}
          />
        ))}

      {hasPromoMessage ? (
        <Box height="40px">
          <PromotionalBlock
            promotion={promotion || {}}
            hasBannerAbove={hasPromoBanner}
          />
        </Box>
      ) : null}

      {promotion.promo_display_type &&
      TIMER_BANNERS.includes(promotion.promo_display_type) ? (
        <PromoTimer promotion={promotion} promotionType={promotionType} />
      ) : null}
    </Flex>
  );
};

export default BrandsPromo;
